import React, { Component } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  withTheme,
  withStyles,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { withModulesManager, journalize, withHistory, formatDateFromISO, formatMessage } from "@openimis/fe-core";
import { fetchAllForms, deleteOdkForm } from "../actions";

const styles = (theme) => ({
  paper: theme.paper.paper,
  title: theme.paper.title,
  page: theme.page,
  dialogContent: {
    minWidth: "400px",
  },
});

class AllForms extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedForm: null,
      openDeleteModal: false,
      openConfigModal: false,
      forms: [],
      hiddenForms: [],
      formConfig: {},
      configValues: [], // Initialize configValues array
    };
  }

  componentDidMount() {
    this.props.fetchAllForms();

    // Load hidden forms from localStorage when the component mounts
    const hiddenFormsFromStorage = JSON.parse(localStorage.getItem("hiddenForms")) || [];
    this.setState({ hiddenForms: hiddenFormsFromStorage });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.allForms !== this.props.allForms) {
      // Filter out invalid forms before updating state
      const validForms = this.props.allForms.filter((form) => form.isValid);
      this.setState({ forms: validForms });
    }
  }

  handleCloseDeleteModal = () => {
    this.setState({ openDeleteModal: false, selectedForm: null });
  };

  handleCloseConfigModal = () => {
    this.setState({ openConfigModal: false, selectedForm: null, formConfig: {}, configValues: [] });
  };

  handleDeleteForm = (form) => {
    this.setState({ selectedForm: form, openDeleteModal: true });
  };

  handleConfirmDelete = () => {
    const { selectedForm } = this.state;

    if (!selectedForm || !selectedForm.uuid) {
      return; // Exit early if UUID is not available
    }

    const hiddenFormsFromStorage = JSON.parse(localStorage.getItem("hiddenForms")) || [];

    // Add the deleted form's UUID to the hidden forms list
    hiddenFormsFromStorage.push(selectedForm.uuid);

    // Save the updated list back to localStorage
    localStorage.setItem("hiddenForms", JSON.stringify(hiddenFormsFromStorage));

    // Set the form to invalid before calling deleteOdkForm
    selectedForm.isValid = false; // Set isValid to false for the deleted form

    // Call the deleteOdkForm action with the UUID (only if valid)
    this.props.deleteOdkForm({ odkFormUuid: selectedForm.uuid }, "deleteOdkForm");

    // Update the state after mutation (assuming successful mutation)
    this.setState((prevState) => {
      const updatedForms = prevState.forms.filter((form) => form.uuid !== selectedForm.uuid);

      return {
        forms: updatedForms, // Remove form from state
        hiddenForms: hiddenFormsFromStorage, // Update hiddenForms in localStorage
        openDeleteModal: false, // Close the delete modal
        selectedForm: null, // Clear the selected form
      };
    });
  };

  handleOpenConfigModal = (form) => {
    // Initialize configValues with existing calculation parameters if available
    const calculationParams = form.odkformconfig?.calculationParameters
      ? form.odkformconfig.calculationParameters
          .replace(/[\[\]"]/g, "")
          .split(",")
          .map((p) => p.trim())
      : [];

    this.setState({
      selectedForm: form,
      openConfigModal: true,
      configValues: calculationParams,
    });
  };

  handleConfigChange = (field, value) => {
    this.setState((prevState) => ({
      formConfig: {
        ...prevState.formConfig,
        [field]: value,
      },
    }));
  };

  handleParamChange = (index, e) => {
    const newValues = [...this.state.configValues];
    newValues[index] = e.target.value;
    this.setState({ configValues: newValues });
  };

  handleRemoveParam = (index) => {
    const newValues = [...this.state.configValues];
    newValues.splice(index, 1);
    this.setState({ configValues: newValues });
  };

  handleAddParam = () => {
    this.setState((prevState) => ({
      configValues: [...prevState.configValues, ""],
    }));
  };

  handleSaveConfigModal = () => {
    const { selectedForm, configValues } = this.state;

    // Prepare the configuration to save
    const updatedConfig = {
      ...selectedForm.odkformconfig,
      calculationParameters: JSON.stringify(configValues),
    };

    // Save the configuration (you can replace this with an API call or Redux action)
    console.log("Saving configuration for form:", selectedForm.uuid, updatedConfig);

    // Close the configuration modal
    this.setState({ openConfigModal: false, selectedForm: null, formConfig: {}, configValues: [] });
  };

  render() {
    const { classes } = this.props;
    const { selectedForm, openDeleteModal, openConfigModal, forms, hiddenForms, configValues } = this.state;

    // Filter out hidden forms
    const visibleForms = forms.filter((form) => !hiddenForms.includes(form.uuid));

    return (
      <>
        <Paper className={classes.paper}>
          <div
            className={classes.title}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "50px" }}
          >
            <Typography>Forms</Typography>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Form Name</TableCell>
                <TableCell>Form Type</TableCell>
                <TableCell>Date Created</TableCell>
                <TableCell>Configurations</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleForms.map((form) => (
                <TableRow key={form.uuid}>
                  <TableCell>{form.formName}</TableCell>
                  <TableCell>{formatMessage(this.props.intl, "quality_assurance_and_accreditation", `form.type.${form.formType}`)}</TableCell>
                  <TableCell>
                    {formatDateFromISO(this.props.modulesManager, this.props.intl, form.createdDate)}
                  </TableCell>
                  <TableCell>
                    <Button variant="contained" color="primary" onClick={() => this.handleOpenConfigModal(form)}>
                      Form Configuration
                    </Button>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => this.handleDeleteForm(form)}>
                      <DeleteIcon style={{ color: "primary" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteModal} onClose={this.handleCloseDeleteModal}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete "{selectedForm?.formName}"?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDeleteModal} color="primary">
              Cancel
            </Button>
            <Button onClick={this.handleConfirmDelete} color="primary" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Configuration Dialog */}
        <Dialog open={openConfigModal} onClose={this.handleCloseConfigModal}>
          <DialogTitle>Add Configurations</DialogTitle>
          <DialogContent>
            <Typography>Configurations for: {selectedForm?.formName}</Typography>

            {/* Check if odkFormconfig is not null */}
            {selectedForm?.odkformconfig ? (
              <>
                <Typography variant="subtitle1">
                  Calculation Parameters: {selectedForm?.odkformconfig.calculationParameters.replace(/[\[\]"]/g, "")}
                </Typography>
                <Typography variant="subtitle1">
                  Created Date:{" "}
                  {selectedForm?.odkformconfig.createdDate
                    ? new Date(selectedForm?.odkformconfig.createdDate).toLocaleDateString()
                    : "N/A"}
                </Typography>
                <Typography variant="subtitle1">
                  Updated Date:{" "}
                  {selectedForm?.odkformconfig.updatedDate
                    ? new Date(selectedForm?.odkformconfig.updatedDate).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No configuration found for this form.
              </Typography>
            )}

            {/* Configuration parameters (configValues) */}
            {configValues.map((param, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                <TextField
                  label={`Parameter ${index + 1}`}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={param}
                  onChange={(e) => this.handleParamChange(index, e)}
                />
                <IconButton onClick={() => this.handleRemoveParam(index)} color="secondary">
                  ✕
                </IconButton>
              </div>
            ))}

            <Button onClick={this.handleAddParam} color="primary" variant="outlined">
              Add Calculation Parameter
            </Button>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.handleCloseConfigModal} color="primary">
              Close
            </Button>
            <Button onClick={this.handleSaveConfigModal} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

const mapStateToProps = (state, props) => ({
  rights: !!state.core?.user?.i_user?.rights,
  mutation: state.quality_assurance_and_accreditation.mutation,
  fetchingAllForms: state.quality_assurance_and_accreditation.fetchingAllForms,
  fetchedAllForms: state.quality_assurance_and_accreditation.fetchedAllForms,
  allForms: state.quality_assurance_and_accreditation.allForms,
  errorAllForms: state.quality_assurance_and_accreditation.errorAllForms,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ journalize, fetchAllForms, deleteOdkForm }, dispatch);

export default withModulesManager(
  injectIntl(withTheme(withStyles(styles)(withHistory(connect(mapStateToProps, mapDispatchToProps)(AllForms))))),
);
